import { useEffect, useMemo, useRef, useState } from 'react';
import { checkDeviceSupport } from '@viewer/controls/TouchControls';
import { resolvePlayCanvasSplatUrl } from '@viewer/lod/resolvePlayCanvasUrl';
import {
  MOBILE_QUALITY_VALUES,
  mobileQualityLabel,
  mobileQualityToEntranceRuntimeOptions,
  mobileQualityToRuntimeOptions,
  parseMobileQuality,
  type MobileQuality,
} from '@viewer/playcanvas/mobileQuality';
import { shouldCommitHudSnapshot } from '@app/hudSnapshotGate';
import {
  mountPlayCanvasViewer,
  type PlayCanvasHudSnapshot,
  type PlayCanvasViewer,
} from '@viewer/playcanvas/mountPlayCanvasViewer';
import {
  mountRevealOnlyViewer,
  type RevealOnlyViewer,
} from '@viewer/playcanvas/mountRevealOnlyViewer';
import { loadWorldScene } from '@viewer/world/loadWorldScene';
import { optimiseWorldSceneForDevice } from '@viewer/world/mobileWorldScene';
import { formatCameraAuthoringBundle } from '@viewer/playcanvas/cameraAuthoring';

function formatSplatsM(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
  return String(n);
}

function PerfChart({ samples, targetMs }: { samples: number[]; targetMs: number }) {
  if (samples.length === 0) return null;
  const maxMs = targetMs * 3;
  return (
    <div className="perf-chart" title={`Render time history. Target: ${targetMs.toFixed(1)} ms`}>
      {samples.map((ms, i) => {
        const pct = Math.min(100, (ms / maxMs) * 100);
        const col = ms < targetMs * 0.5 ? '#44cc66' : ms <= targetMs ? '#cccc22' : '#dd3322';
        return (
          <div key={i} className="perf-bar" style={{ height: `${pct}%`, backgroundColor: col }} />
        );
      })}
      <div className="perf-target-line" />
    </div>
  );
}

export function parseQuery(
  search: string,
  routeWorldId?: string,
): {
  worldId: string;
  revealOnly: boolean;
  revealUrl: string | null;
  mobileMedia: boolean;
  adminMode: boolean;
} {
  const params = new URLSearchParams(search);
  const mediaParam = params.get('mobileMedia') ?? params.get('media');
  return {
    worldId: routeWorldId ?? params.get('world') ?? 'lair',
    revealOnly: params.get('revealOnly') === '1',
    revealUrl: params.get('revealUrl'),
    mobileMedia: mediaParam !== '0',
    adminMode: params.get('admin') === '1',
  };
}

export function resolveMobileVideoAutoplay(
  quality: MobileQuality,
  userAllowsAutoplay: boolean,
): boolean {
  return quality !== 'low' && userAllowsAutoplay;
}

export function PlayCanvasApp({
  worldId,
  mobileLayout = false,
}: {
  worldId?: string;
  mobileLayout?: boolean;
}) {
  const query = useMemo(() => parseQuery(window.location.search, worldId), [worldId]);
  const deviceSupport = useMemo(() => checkDeviceSupport(), []);
  const hostRef = useRef<HTMLDivElement | null>(null);
  const canvasShellRef = useRef<HTMLElement | null>(null);
  const runtimeRef = useRef<PlayCanvasViewer | RevealOnlyViewer | null>(null);
  const [debugOpen, setDebugOpen] = useState(query.adminMode);
  const [cameraCopyStatus, setCameraCopyStatus] = useState('');
  const [hud, setHud] = useState<PlayCanvasHudSnapshot | null>(null);
  const debugOpenRef = useRef(debugOpen);
  const hudGateRef = useRef<{
    snapshot: PlayCanvasHudSnapshot | null;
    committedAtMs: number;
  }>({ snapshot: null, committedAtMs: 0 });
  const [configError, setConfigError] = useState<string | null>(null);
  const [mobileQualityOpen, setMobileQualityOpen] = useState(false);
  const [mobileQuality, setMobileQuality] = useState<MobileQuality>(() => {
    const params = new URLSearchParams(window.location.search);
    const queryQuality = params.get('quality') ?? params.get('q');
    if (queryQuality) return parseMobileQuality(queryQuality);
    try {
      return parseMobileQuality(window.localStorage.getItem('gii:mobile-quality'));
    } catch {
      return 'normal';
    }
  });
  const mobileQualityRef = useRef(mobileQuality);
  const [mobileVideoAutoplay, setMobileVideoAutoplay] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const queryAutoplay = params.get('videoAutoplay') ?? params.get('autoplay');
    if (queryAutoplay === '0') return false;
    if (queryAutoplay === '1') return true;
    try {
      return window.localStorage.getItem('gii:mobile-video-autoplay') !== '0';
    } catch {
      return true;
    }
  });
  const mobileVideoAutoplayRef = useRef(mobileVideoAutoplay);
  const effectiveMobileVideoAutoplay = mobileLayout
    ? resolveMobileVideoAutoplay(mobileQuality, mobileVideoAutoplay)
    : true;
  const worldReady = hud?.worldReady === true;
  const [sceneConfig, setSceneConfig] = useState<{
    splatUrl: string;
    cameraStart: [number, number, number];
    cameraYaw: number;
    collisionUrl?: string;
    sceneTransform: {
      position: [number, number, number];
      rotation: [number, number, number];
      scale: [number, number, number];
    };
    worldScene: Awaited<ReturnType<typeof loadWorldScene>>;
  } | null>(null);

  useEffect(() => {
    if (!mobileLayout) return;
    document.documentElement.classList.add('mobile-viewer');
    return () => {
      document.documentElement.classList.remove('mobile-viewer');
    };
  }, [mobileLayout]);

  useEffect(() => {
    debugOpenRef.current = debugOpen;
  }, [debugOpen]);

  useEffect(() => {
    if (!mobileLayout) return;
    mobileQualityRef.current = mobileQuality;
    try {
      window.localStorage.setItem('gii:mobile-quality', mobileQuality);
    } catch {
      // Ignore private-mode storage failures.
    }
  }, [mobileLayout, mobileQuality]);

  useEffect(() => {
    if (!mobileLayout) return;
    mobileVideoAutoplayRef.current = mobileVideoAutoplay;
    try {
      window.localStorage.setItem('gii:mobile-video-autoplay', mobileVideoAutoplay ? '1' : '0');
    } catch {
      // Ignore private-mode storage failures.
    }
  }, [mobileLayout, mobileVideoAutoplay]);

  useEffect(() => {
    if (!mobileLayout || query.revealOnly || !worldReady) return;
    const viewer = runtimeRef.current;
    if (viewer && 'setQuality' in viewer) {
      viewer.setQuality(mobileQualityToRuntimeOptions(mobileQuality));
    }
  }, [mobileLayout, mobileQuality, query.revealOnly, worldReady]);

  useEffect(() => {
    if (!mobileLayout || query.revealOnly) return;
    const viewer = runtimeRef.current;
    if (viewer && 'setMediaAutoplay' in viewer) {
      viewer.setMediaAutoplay(effectiveMobileVideoAutoplay);
    }
  }, [effectiveMobileVideoAutoplay, mobileLayout, query.revealOnly]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.code === 'Tab') {
        event.preventDefault();
        setDebugOpen((prev) => !prev);
      }
      if (event.code === 'KeyM') {
        event.preventDefault();
        event.stopPropagation();
        const viewer = runtimeRef.current;
        if (viewer && 'toggleMeasureEdit' in viewer) viewer.toggleMeasureEdit();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  useEffect(() => {
    if (!mobileLayout) return;
    const relayout = () => runtimeRef.current?.resize();
    window.addEventListener('orientationchange', relayout);
    window.visualViewport?.addEventListener('resize', relayout);
    requestAnimationFrame(relayout);
    return () => {
      window.removeEventListener('orientationchange', relayout);
      window.visualViewport?.removeEventListener('resize', relayout);
    };
  }, [mobileLayout, sceneConfig]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const worldScene = await loadWorldScene(query.worldId);
        if (cancelled) return;
        const runtimeWorldScene = mobileLayout
          ? optimiseWorldSceneForDevice(
              {
                ...worldScene,
                pointPreview: worldScene.mobilePointPreview ?? worldScene.pointPreview,
              },
              { mobileLayout: true, retainMediaPanels: query.mobileMedia },
            )
          : worldScene;
        setSceneConfig({
          splatUrl: resolvePlayCanvasSplatUrl(worldScene.splatUrl, worldScene.splatOption2, {
            streaming: worldScene.splatStreaming,
            mobile: mobileLayout,
            mobileSplat: worldScene.mobileSplatUrl,
          }),
          cameraStart: worldScene.cameraStart,
          cameraYaw: worldScene.cameraYaw,
          collisionUrl: worldScene.collisionUrl,
          sceneTransform: {
            position: worldScene.position,
            rotation: worldScene.rotation,
            scale: worldScene.scale,
          },
          worldScene: runtimeWorldScene,
        });
      } catch (err) {
        if (!cancelled) {
          setConfigError(err instanceof Error ? err.message : 'Failed to load world config');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mobileLayout, query.mobileMedia, query.worldId]);

  useEffect(() => {
    if (!hostRef.current || !sceneConfig) return;

    let viewer: PlayCanvasViewer | RevealOnlyViewer | null = null;
    let cancelled = false;
    hudGateRef.current = { snapshot: null, committedAtMs: 0 };
    const initialQuality = mobileLayout
      ? mobileQualityToEntranceRuntimeOptions(mobileQualityRef.current)
      : undefined;
    const initialMediaAutoplay = mobileLayout
      ? resolveMobileVideoAutoplay(mobileQualityRef.current, mobileVideoAutoplayRef.current)
      : true;

    void (async () => {
      try {
        if (query.revealOnly) {
          viewer = await mountRevealOnlyViewer(hostRef.current!, {
            splatUrl: query.revealUrl ?? sceneConfig.splatUrl,
            cameraStart: sceneConfig.cameraStart,
            cameraYawDeg: sceneConfig.cameraYaw,
            worldScene: sceneConfig.worldScene,
            layoutRoot: canvasShellRef.current ?? undefined,
          });
        } else {
          viewer = await mountPlayCanvasViewer(hostRef.current!, {
            splatUrl: sceneConfig.splatUrl,
            cameraStart: sceneConfig.cameraStart,
            cameraYawDeg: sceneConfig.cameraYaw,
            collisionUrl: sceneConfig.collisionUrl,
            sceneTransform: sceneConfig.sceneTransform,
            worldScene: sceneConfig.worldScene,
            layoutRoot: canvasShellRef.current ?? undefined,
            worldId: query.worldId,
            initialQuality,
            initialMediaAutoplay,
            mobileLayout,
            onHud: (snapshot) => {
              if (cancelled) return;
              const gate = hudGateRef.current;
              const now = performance.now();
              if (
                !shouldCommitHudSnapshot(gate.snapshot, snapshot, {
                  debugOpen: debugOpenRef.current,
                  nowMs: now,
                  lastCommittedMs: gate.committedAtMs,
                })
              ) {
                return;
              }
              gate.snapshot = snapshot;
              gate.committedAtMs = now;
              setHud(snapshot);
            },
          });
        }
        if (!cancelled) {
          runtimeRef.current = viewer;
          if (!query.revealOnly && debugOpenRef.current && 'setDebugPerf' in viewer) {
            viewer.setDebugPerf(true);
          }
        } else {
          viewer?.dispose();
        }
      } catch (err) {
        console.error('[GoIntoIt] mount failed:', err);
        if (!cancelled) {
          setHud({
            loadState: 'error',
            renderedSplats: 0,
            splatBudget: 0,
            lodRangeMin: 0,
            lodRangeMax: 0,
            streamingPhase: 'booting',
            streamingFill: 0,
            loadProgress: 0,
            entrancePhase: 'welcome',
            entranceProgress: 0,
            entranceDotsFill: 0,
            worldReady: false,
            collisionLoaded: false,
            pointerLocked: false,
            touchMode: false,
            droneMode: false,
            fps: 0,
            frameTimeMs: 0,
            renderTimeMs: 0,
            overlayRenderMs: 0,
            devicePixelRatio: 1,
            gsplatWorkMs: 0,
            renderHistory: [],
            graphicsApi: 'webgl2',
            gsplatRenderer: 'auto',
            engineFrameMs: 0,
            drawCallsTotal: 0,
            drawCallsForward: 0,
            gsplatSortMs: 0,
            gsplatBufferCopyPct: 0,
            vramMb: 0,
            forwardRenderMs: 0,
            errorMessage: err instanceof Error ? err.message : 'Viewer init failed',
          });
        }
      }
    })();

    return () => {
      cancelled = true;
      runtimeRef.current = null;
      viewer?.dispose();
    };
  }, [sceneConfig, query.revealOnly, query.revealUrl, query.worldId, mobileLayout]);

  useEffect(() => {
    if (query.revealOnly) return;
    const viewer = runtimeRef.current;
    if (viewer && 'setDebugPerf' in viewer) viewer.setDebugPerf(debugOpen);
  }, [debugOpen, query.revealOnly]);

  const revealOnly = query.revealOnly;
  const entranceActive = !revealOnly && (hud ? !hud.worldReady : false);

  const copyCameraAuthoringJson = async () => {
    const viewer = runtimeRef.current;
    if (!viewer || !('getCameraAuthoringSnapshot' in viewer)) {
      setCameraCopyStatus('Camera is not ready');
      return;
    }
    const snapshot = viewer.getCameraAuthoringSnapshot();
    if (!snapshot) {
      setCameraCopyStatus('Camera is not ready');
      return;
    }
    const text = formatCameraAuthoringBundle(snapshot);
    try {
      await navigator.clipboard.writeText(text);
      setCameraCopyStatus('Copied authoring JSON');
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      const copied = document.execCommand('copy');
      textarea.remove();
      setCameraCopyStatus(copied ? 'Copied authoring JSON' : 'Clipboard unavailable');
    }
  };
  const desktopViewerLayout = !mobileLayout && !revealOnly;
  const shellClass = mobileLayout
    ? 'app-shell app-shell--mobile-viewer'
    : desktopViewerLayout
      ? 'app-shell app-shell--desktop-viewer'
      : 'app-shell';

  if (!deviceSupport.supported) {
    return (
      <main className={shellClass}>
        <section className="hero">
          <h1>Go Into It</h1>
          <p className="device-unsupported">{deviceSupport.reason}</p>
        </section>
      </main>
    );
  }

  return (
    <main
      className={[shellClass, hud?.worldReady && 'app-shell--world-ready']
        .filter(Boolean)
        .join(' ')}
    >
      {!mobileLayout ? (
        revealOnly ? (
          <section className="hero">
            <h1>Reveal test — {query.worldId}</h1>
            <p>
              Stripped viewer: splat load + <code>GsplatRevealRadial</code> only. Params:{' '}
              <code>?revealOnly=1</code>, <code>?renderer=cpu</code>,{' '}
              <code>?revealUrl=…lod-meta.json</code>. See <code>docs/reveal-handoff.md</code>.
            </p>
            {configError ? <p className="device-unsupported">{configError}</p> : null}
          </section>
        ) : configError ? (
          <p className="device-unsupported desktop-viewer-error">{configError}</p>
        ) : null
      ) : configError ? (
        <p className="device-unsupported mobile-viewer-error">{configError}</p>
      ) : null}
      <section
        className={[
          'canvas-shell',
          mobileLayout && 'canvas-shell--mobile',
          entranceActive && 'canvas-shell--entrance',
        ]
          .filter(Boolean)
          .join(' ')}
        aria-label="Viewer"
        ref={canvasShellRef}
      >
        {hud?.droneMode ? (
          <div className="drone-badge" aria-live="polite">
            DRONE
          </div>
        ) : null}
        {mobileLayout && !query.revealOnly ? (
          <div className="mobile-quality">
            <button
              className="mobile-quality-btn"
              type="button"
              title="Quality"
              aria-label="Quality"
              aria-expanded={mobileQualityOpen}
              aria-haspopup="menu"
              onClick={() => setMobileQualityOpen((open) => !open)}
            >
              <svg
                viewBox="0 0 24 24"
                width="19"
                height="19"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                aria-hidden="true"
              >
                <path d="M4 7h9" />
                <path d="M17 7h3" />
                <path d="M4 17h3" />
                <path d="M11 17h9" />
                <circle cx="15" cy="7" r="2" />
                <circle cx="9" cy="17" r="2" />
              </svg>
            </button>
            {mobileQualityOpen ? (
              <div className="mobile-quality-menu" role="menu">
                {MOBILE_QUALITY_VALUES.map((quality) => (
                  <button
                    key={quality}
                    className={quality === mobileQuality ? 'selected' : undefined}
                    type="button"
                    role="menuitemradio"
                    aria-checked={quality === mobileQuality}
                    onClick={() => {
                      setMobileQuality(quality);
                      setMobileQualityOpen(false);
                    }}
                  >
                    {mobileQualityLabel(quality)}
                  </button>
                ))}
                <div className="mobile-quality-divider" />
                <button
                  className="mobile-quality-toggle"
                  type="button"
                  role="switch"
                  aria-checked={effectiveMobileVideoAutoplay}
                  disabled={mobileQuality === 'low'}
                  onClick={() => setMobileVideoAutoplay((value) => !value)}
                >
                  <span
                    className={[
                      'mobile-quality-switch',
                      effectiveMobileVideoAutoplay && 'mobile-quality-switch--on',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    aria-hidden="true"
                  >
                    <span />
                  </span>
                  <span className="mobile-quality-toggle-copy">
                    <span>Autoplay videos</span>
                    <small>
                      {mobileQuality === 'low'
                        ? 'Off in low'
                        : effectiveMobileVideoAutoplay
                          ? 'On'
                          : 'Tap to play'}
                    </small>
                  </span>
                </button>
              </div>
            ) : null}
          </div>
        ) : null}
        <div className="canvas-host" ref={hostRef} />

        {mobileLayout && hud?.loadState === 'error' ? (
          <div className="entrance-status entrance-status--error" aria-live="assertive">
            {hud.errorMessage ?? 'Viewer failed to load'}
          </div>
        ) : null}

        {debugOpen && hud ? (
          <div className="canvas-overlay">
            <div>FPS: {hud.fps.toFixed(1)}</div>
            <div>Frame: {hud.frameTimeMs.toFixed(2)} ms</div>
            <div>GSplat work: {hud.gsplatWorkMs.toFixed(2)} ms</div>
            <div>DPR cap: {hud.devicePixelRatio.toFixed(2)}</div>
            <div>Entrance: {hud.entrancePhase}</div>
            <div>Graphics: {hud.graphicsApi}</div>
            <div>World ready: {hud.worldReady ? 'yes' : 'no'}</div>
            <div>
              Splats: {formatSplatsM(hud.renderedSplats)} / {formatSplatsM(hud.splatBudget)}
            </div>
            <div>Streaming: {(hud.streamingFill * 100).toFixed(0)}%</div>
            <div>
              LOD: {hud.lodRangeMin}–{hud.lodRangeMax}
            </div>
          </div>
        ) : null}

        {debugOpen && hud ? (
          <aside className="debug-panel" aria-label="Debug overlay panel">
            {query.adminMode ? (
              <section className="camera-authoring" aria-label="Camera authoring">
                <div className="camera-authoring__heading">
                  <span>Authoring camera</span>
                  <span>live</span>
                </div>
                {hud.cameraAuthoring ? (
                  <div className="camera-authoring__readout">
                    <div>
                      <span>Position</span>
                      <code>
                        {hud.cameraAuthoring.position.map((n) => n.toFixed(3)).join(', ')}
                      </code>
                    </div>
                    <div>
                      <span>Rotation</span>
                      <code>
                        {hud.cameraAuthoring.rotation.map((n) => n.toFixed(2)).join(', ')}
                      </code>
                    </div>
                    <div>
                      <span>FOV</span>
                      <code>{hud.cameraAuthoring.fov.toFixed(1)}°</code>
                    </div>
                  </div>
                ) : null}
                <button type="button" onClick={() => void copyCameraAuthoringJson()}>
                  Copy camera JSON
                </button>
                <small>
                  Includes spawn values, a keyframe aimed 5 m through the crosshair, and a panel
                  seed 2 m ahead.
                </small>
                <div className="camera-authoring__status" role="status" aria-live="polite">
                  {cameraCopyStatus}
                </div>
              </section>
            ) : null}
            <h2>Performance</h2>
            <p>Toggle: Tab · Fullscreen: button or Escape · MiniStats graph: bottom-left</p>
            <div className="debug-grid">
              <div>FPS</div>
              <div>{hud.fps.toFixed(1)}</div>
              <div>Frame ms</div>
              <div>{hud.frameTimeMs.toFixed(2)}</div>
              <div>Engine frame</div>
              <div>{hud.engineFrameMs.toFixed(2)} ms</div>
              <div>GSplat ms</div>
              <div
                style={{
                  color:
                    hud.gsplatWorkMs > 12
                      ? '#ff8866'
                      : hud.gsplatWorkMs > 6
                        ? '#ffdd44'
                        : '#66dd88',
                }}
              >
                {hud.gsplatWorkMs.toFixed(2)}
              </div>
              <div>Forward render</div>
              <div>{hud.forwardRenderMs.toFixed(2)} ms</div>
              <div>GSplat sort</div>
              <div>{hud.gsplatSortMs.toFixed(2)} ms</div>
              <div>Draw calls</div>
              <div>
                {hud.drawCallsTotal} ({hud.drawCallsForward} fwd)
              </div>
              <div>VRAM</div>
              <div>{hud.vramMb.toFixed(1)} MB</div>
              <div>Buffer copy</div>
              <div>{hud.gsplatBufferCopyPct.toFixed(0)}%</div>
              <div>Budget fill</div>
              <div>{(hud.streamingFill * 100).toFixed(0)}%</div>
            </div>
            <PerfChart samples={hud.renderHistory} targetMs={16.7} />
            <div className="debug-grid">
              <div>Entrance phase</div>
              <div>{hud.entrancePhase}</div>
              <div>Entrance load</div>
              <div>{(hud.entranceProgress * 100).toFixed(0)}%</div>
              <div>Stream phase</div>
              <div>{hud.streamingPhase}</div>
              <div>Streaming fill</div>
              <div>{(hud.streamingFill * 100).toFixed(0)}%</div>
              <div>Splat budget</div>
              <div>{formatSplatsM(hud.splatBudget)}</div>
              <div>Rendered</div>
              <div>{formatSplatsM(hud.renderedSplats)}</div>
              <div>LOD range</div>
              <div>
                {hud.lodRangeMin}–{hud.lodRangeMax}
              </div>
              <div>Graphics</div>
              <div>{hud.graphicsApi}</div>
              <div>GSplat renderer</div>
              <div>{hud.gsplatRenderer}</div>
              <div>DPR cap</div>
              <div>{hud.devicePixelRatio.toFixed(2)}</div>
              <div>Collision</div>
              <div>{hud.collisionLoaded ? 'loaded' : 'none'}</div>
              <div>Drone</div>
              <div>{hud.droneMode ? 'on' : 'off'}</div>
              <div>World ready</div>
              <div>{hud.worldReady ? 'yes' : 'no'}</div>
            </div>
          </aside>
        ) : null}
      </section>
    </main>
  );
}
